/*
 * Copyright (c) CIRROM 2024.
 */

import { API } from "./api";

export interface SyncDetails {
  system_a_id: number;
  system_a_name: string;
  system_b_id: number;
  system_b_name: string;
  total_contacts_in_scope: number;
  total_contacts_matched: number;
  total_contacts_proposed: number;
  total_contacts_unmatched: number;
  total_contacts_with_unmatched_fields: number;
}

export interface SyncRecord {
  url: string;
  company: string;
  first_name: string;
  last_name: string;
  primary_email: string;
  job_title: string;
  field_value: string;
  other_system_id: string;
}

export interface SyncUnmatchedRecord {
  url: string;
  company: string;
  first_name: string;
  last_name: string;
  primary_email: string;
  job_title: string;
  crm_entity_id: string;
  cirrom_entity_id: number;
}

export interface SyncGroup {
  sync_group_id: number;
  match_source: string;
  system_a_name: string;
  system_b_name: string;
  system_a_record: SyncRecord;
  system_b_record: SyncRecord;
}

export interface SyncLinkageProposal {
  proposal_id: number;
  match_source: string;
  system_b_record: SyncRecord;
}

export interface SyncLinkageProposalGroup {
  matching_id: number;
  system_a_name: string;
  system_b_name: string;
  system_a_record: SyncRecord;
  rows: SyncLinkageProposal[];
}

export interface SyncLinkageFilters {
  emailsAreTheSameFilter: boolean;
  firstNamesAreTheSameFilter: boolean;
  lastNamesAreTheSameFilter: boolean;
  jobTitlesAreTheSameFilter: boolean;
  companiesAreSimilarFilter: boolean;
  oneHasNoEmailFilter: boolean;
  oneHasNoCompanyFilter: boolean;
  includeCrmFieldProposals: boolean;
  includeCirromProposals: boolean;
  bothAdditionalAttributesBlank: boolean;
}

export interface SyncFieldFilters {
  casingDifferenceFilter: boolean;
  oneIsBlankFilter: boolean;
  haveCompaniesFilter: boolean;
  companiesMatchFilter: boolean;
}

export interface SyncFieldMatching {
  sync_groups: SyncGroup[];
  sync_fields: string[];
}

export interface SyncChange {
  field: string;
  old_value: string;
  new_value: string;
  sync_group_id?: number;
}

export interface SyncProjectOption {
  name: string;
  value: string;
}

export interface SyncProject {
  sync_group_id?: number;
  sync_system_id?: number;
  name: string;
  crm_system_id: number;
  sync_system_type: string;
  options: SyncProjectOption[];
}

export interface EventSyncDashboardEvent {
  event_id: string;
  event_name: string;
  crm_list_id?: string;
  crm_list_name?: string;
  sync_contacts: number;
  unmanaged_event_contacts: number;
  contact_diffs: number;
}

export interface EventSyncDashboard {
  sync_group_id: number;
  crm_system_id: number;
  crm_system_name: string;
  event_system_id: number;
  event_system_name: string;
  sync_contacts: number;
  unmanaged_event_contacts: number;
  contact_diffs: number;
  events: EventSyncDashboardEvent[];
}

export interface EventSyncCrmListOption {
  list_id: string;
  list_name: string;
}

export interface EventSyncEventOption {
  event_id: string;
  event_name: string;
}

export interface EventSyncConfigOptions {
  crm_lists: EventSyncCrmListOption[];
  events: EventSyncEventOption[];
  source_activity_types: EventSyncSourceActivityTypeOption[];
  target_activity_types: EventSyncTargetActivityTypeOption[];
  activity_mappings: EventSyncActivityTypeMapping[];
}

export interface EventSyncSourceActivityTypeOption {
  value: string;
  label: string;
  family: string;
}

export interface EventSyncTargetActivityTypeOption {
  activityTypeId: string;
  name: string;
  group?: string;
  isSearchable?: boolean;
}

export interface EventSyncActivityTypeMapping {
  mapping_id?: number;
  source_activity_type: string;
  target_activity_type_id: string;
  target_activity_type_name: string;
}

export interface EventSyncFieldOption {
  value: string;
  label: string;
}

export interface EventSyncFieldConfig {
  field_config_id?: number | null;
  crm_field: string;
  event_field: string;
}

export interface EventSyncFieldConfigResponse {
  fields: EventSyncFieldConfig[];
  available_fields: EventSyncFieldOption[];
}

export interface EventSyncEventConfig {
  sync_group_id: number;
  crm_list_id: string;
  crm_list_name: string;
  event_id: string;
  event_name: string;
}

export const getSyncResultsForSystemId = async (
  systemId: number,
  dataType: string
): Promise<SyncDetails> => {
  const response = await API.get("/get-sync-for-system", {
    params: {
      system_id: systemId,
      data_type: dataType,
    },
  });
  return response.data;
};

export const getEventSyncDashboard = async (
  syncGroupId: number
): Promise<EventSyncDashboard> => {
  const response = await API.get("/get-event-sync-dashboard", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};

export const deleteEventSyncEvent = async (
  syncGroupId: number,
  eventId: string
): Promise<{ sync_group_id: number; event_id: string; deleted: number }> => {
  const response = await API.delete("/delete-event-sync-event", {
    params: { sync_group_id: syncGroupId, event_id: eventId },
  });
  return response.data;
};

export const getEventSyncConfigOptions = async (
  syncGroupId: number
): Promise<EventSyncConfigOptions> => {
  const response = await API.get("/get-event-sync-config-options", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};

export const getEventSyncFieldConfig = async (
  syncGroupId: number
): Promise<EventSyncFieldConfigResponse> => {
  const response = await API.get("/get-event-sync-field-config", {
    params: { sync_group_id: syncGroupId },
  });
  return response.data;
};

export const saveEventSyncFieldConfig = async (
  syncGroupId: number,
  fields: EventSyncFieldConfig[]
): Promise<EventSyncFieldConfigResponse> => {
  const response = await API.post("/save-event-sync-field-config", {
    sync_group_id: syncGroupId,
    fields,
  });
  return response.data;
};

export const upsertEventSyncEvent = async (
  config: EventSyncEventConfig
): Promise<EventSyncEventConfig & { id: number }> => {
  const response = await API.put("/upsert-event-sync-event", config);
  return response.data;
};

export const saveEventSyncActivityTypeConfig = async (
  syncGroupId: number,
  mappings: EventSyncActivityTypeMapping[]
): Promise<{
  sync_group_id: number;
  activity_mappings: EventSyncActivityTypeMapping[];
}> => {
  const response = await API.post("/save-event-sync-activity-type-config", {
    sync_group_id: syncGroupId,
    mappings,
  });
  return response.data;
};

export const rerunSyncGroup = async (
  syncGroupId: number
): Promise<{ sync_group_id: number; status: string }> => {
  const response = await API.post("/rerun-sync-group", {
    sync_group_id: syncGroupId,
  });
  return response.data;
};

export const upsertSyncProject = async (
  syncProject: SyncProject
): Promise<SyncProject> => {
  const response = await API.put("/upsert-sync-project", syncProject);
  return response.data;
};

export const getSyncLinkageProposals = async (
  systemId: number,
  dataType: string,
  filters: SyncLinkageFilters,
  singleOptionFilter: boolean
): Promise<SyncLinkageProposalGroup[]> => {
  const response = await API.get("/get-sync-linkage-proposals-for-system", {
    params: {
      system_id: systemId,
      data_type: dataType,
      emails_are_the_same: filters.emailsAreTheSameFilter,
      first_names_are_the_same: filters.firstNamesAreTheSameFilter,
      last_names_are_the_same: filters.lastNamesAreTheSameFilter,
      job_titles_are_the_same: filters.jobTitlesAreTheSameFilter,
      companies_are_similar: filters.companiesAreSimilarFilter,
      one_has_no_email: filters.oneHasNoEmailFilter,
      one_has_no_company: filters.oneHasNoCompanyFilter,
      include_crm_field_proposals: filters.includeCrmFieldProposals,
      include_cirrom_proposals: filters.includeCirromProposals,
      both_additional_attributes_blank: filters.bothAdditionalAttributesBlank,
      single_options_filter: singleOptionFilter,
    },
  });
  return response.data;
};

export const getSyncUnmatchedContacts = async (
  systemId: number,
  dataType: string
): Promise<SyncUnmatchedRecord[]> => {
  const response = await API.get("/get-sync-unmatched-contacts-for-system", {
    params: {
      system_id: systemId,
      data_type: dataType,
    },
  });
  return response.data;
};

export const searchForContactsFromOtherSystem = async (
  systemId: number,
  firstName: string,
  lastName: string,
  email: string
): Promise<SyncUnmatchedRecord[]> => {
  const response = await API.get("/search-contacts-for-other-system", {
    params: {
      system_id: systemId,
      first_name: firstName,
      last_name: lastName,
      email: email,
    },
  });
  return response.data;
};

export const getSyncFieldMatching = async (
  systemId: number,
  dataType: string,
  fieldFilter: string,
  filter: SyncFieldFilters
): Promise<SyncFieldMatching> => {
  const response = await API.get("/get-sync-field-matching-for-system", {
    params: {
      system_id: systemId,
      data_type: dataType,
      field_filter: fieldFilter,
      casing_difference_filter: filter.casingDifferenceFilter,
      one_is_blank_filter: filter.oneIsBlankFilter,
      have_companies_filter: filter.haveCompaniesFilter,
      companies_match_filter: filter.companiesMatchFilter,
    },
  });
  return response.data;
};

export const approveSyncLinkageProposal = async (
  systemId: number,
  proposalId: number
): Promise<any> => {
  const response = await API.post("/approve-sync-linkage-proposal", {
    system_id: systemId,
    proposal_id: proposalId,
  });
  return response.data;
};

export const rejectSyncLinkageProposal = async (
  systemId: number,
  proposalId: number
): Promise<any> => {
  const response = await API.post("/reject-sync-linkage-proposal", {
    system_id: systemId,
    proposal_id: proposalId,
  });
  return response.data;
};

export const deleteSyncLinkage = async (
  systemId: number,
  linkId: number
): Promise<SyncGroup[]> => {
  const response = await API.post("/delete-sync-linkage", {
    system_id: systemId,
    linkage_id: linkId,
  });
  return response.data;
};

export const forceMatchSyncLinkage = async (
  systemId: number,
  system_a_cirrom_id: number,
  system_b_cirrom_id: number
): Promise<SyncGroup[]> => {
  const response = await API.post("/force-match-sync-linkage", {
    system_id: systemId,
    system_a_cirrom_id: system_a_cirrom_id,
    system_b_cirrom_id: system_b_cirrom_id,
  });
  return response.data;
};

export const approveSyncLinkageProposals = async (
  systemId: number,
  proposalIds: number[]
): Promise<any> => {
  const response = await API.post("/approve-sync-linkage-proposals", {
    system_id: systemId,
    proposals: proposalIds,
  });
  return response.data;
};

export const rejectSyncLinkageProposals = async (
  systemId: number,
  proposalIds: number[]
): Promise<any> => {
  const response = await API.post("/reject-sync-linkage-proposals", {
    system_id: systemId,
    proposal_ids: proposalIds,
  });
  return response.data;
};

export const approveSyncFields = async (
  systemId: number,
  syncGroupId: number,
  systemAChanges: SyncChange[],
  systemBChanges: SyncChange[]
): Promise<SyncGroup[]> => {
  const response = await API.post("/approve-sync-field-changes", {
    system_id: systemId,
    sync_group_id: syncGroupId,
    system_a_changes: systemAChanges,
    system_b_changes: systemBChanges,
  });
  return response.data;
};

export const approveMultipleSyncFields = async (
  systemId: number,
  systemAChanges: SyncChange[],
  systemBChanges: SyncChange[]
): Promise<SyncGroup[]> => {
  const response = await API.post("/approve-multiple-sync-field-changes", {
    system_id: systemId,
    system_a_changes: systemAChanges,
    system_b_changes: systemBChanges,
  });
  return response.data;
};

export const getSyncFieldChangeMetrics = async (
  systemId: number,
  dataType: string
) => {
  const response = await API.get("/get-field-sync-change-metrics-for-system", {
    params: {
      integrated_system_id: systemId,
      data_type: dataType,
    },
  });
  return response.data;
};

export interface SyncIntegratedSystem {
  sync_group_id: number;
  system_a_id: number;
  system_a_name: string;
  system_a_type: string;
  system_b_id: number;
  system_b_name: string;
  system_b_type: string;
  matched_count: number;
  sync_data_type: "CRM" | "EVENTS" | "CONTENT" | "MAILBOX";
}

export type SyncSlotDataType = "CRM" | "CONTENT" | "MAILBOX";

export interface SyncIntegrationOptionSystem {
  id: number;
  name: string;
  system_type: string;
}

export interface SyncIntegrationPairing {
  id: string;
  label: string;
  slot_a_data_type: SyncSlotDataType;
  slot_b_data_type: SyncSlotDataType;
  runnable: boolean;
  slot_a_available_system_ids: number[];
  slot_b_available_system_ids: number[];
}

export interface SyncIntegrationOptions {
  pairings: SyncIntegrationPairing[];
  systems_by_data_type: Record<SyncSlotDataType, SyncIntegrationOptionSystem[]>;
}

export const getAllSyncIntegratedSystems = async (): Promise<
  SyncIntegratedSystem[]
> => {
  const response = await API.get("/get-all-sync-integrated-systems");
  return response.data;
};

export const getAllSyncIntegrationOptions =
  async (): Promise<SyncIntegrationOptions> => {
    const response = await API.get("/get-all-sync-integration-options");
    return response.data;
  };

export const syncIntegratedSystems = async (
  systemASystemId: number,
  systemBSystemId: number
): Promise<{ sync_group_id: number }> => {
  const response = await API.put("/sync-integrated-systems", {
    system_a_system_id: systemASystemId,
    system_b_system_id: systemBSystemId,
  });
  return response.data;
};

export const getSyncLinkageChangeMetrics = async (
  systemId: number,
  dataType: string
) => {
  const response = await API.get(
    "/get-linkage-sync-change-metrics-for-system",
    {
      params: {
        integrated_system_id: systemId,
        data_type: dataType,
      },
    }
  );
  return response.data;
};
